/**
 * A host's map view as geography, as their audience follows it (docs/features/follow-host-map.md): where the centre of
 * the view meets the ground, how much of the land the frame spans there, and which way it looks. Renderer-neutral and
 * screen-neutral, so it means the same on the play world and on Mapbox, on a wide desktop or an upright phone. It
 * travels as LiveKit data on the stage's topic: lossy while the view moves, reliable once it settles and as a heartbeat.
 */

export type HostViewBasemap = 'world' | 'satellite' | 'standard'

/** What a renderer reads off its camera. */
export interface HostViewPose {
  /** Where the centre of the view meets the ground. */
  latitude: number
  longitude: number
  /** How far above the ground that point is, metres: 0 on the ground; a free flight looking at the sky aims higher. */
  lift: number
  /** The height of the frame at that point, metres (2 × distance × tan(½ vertical field of view)). */
  span: number
  /** The host's map width over its height. */
  aspect: number
  /** Which way the view looks, degrees clockwise from north. */
  bearing: number
  /** Degrees from straight down: 0 looks down, 90 at the horizon. */
  pitch: number
  /** The tracked athlete whose card the host has open, if any: their followers see it open too. */
  athlete?: string | null
}

export interface HostView extends HostViewPose {
  /** Counts up with every message a tab sends, so an older one arriving late is dropped. */
  seq: number
  /** The host's clock when it was read, milliseconds. */
  at: number
  basemap: HostViewBasemap
  /** Sent reliably, at rest or as the heartbeat: what a follower under reduced motion cuts to. */
  settled: boolean
}

/** The stage's topic for its host's map view. */
export function hostViewTopic(stageId: string): string {
  return `map.${stageId}`
}

const BASEMAPS: readonly HostViewBasemap[] = ['world', 'satellite', 'standard']
const round = (value: number, places: number) => Math.round(value * 10 ** places) / 10 ** places

/**
 * About 110 bytes of JSON (165 with an athlete’s card open); short keys, numbers rounded to what anyone could see
 * (centimetres, hundredths of a degree).
 */
export function encodeHostView(view: HostView): Uint8Array {
  const wire = {
    v: 1,
    s: view.seq,
    t: Math.round(view.at),
    la: round(view.latitude, 7),
    lo: round(view.longitude, 7),
    h: round(view.lift, 1),
    sp: round(view.span, 1),
    a: round(view.aspect, 3),
    b: round(view.bearing, 2),
    p: round(view.pitch, 2),
    m: view.basemap,
    ...(view.athlete ? { ath: view.athlete } : {}),
    ...(view.settled ? { r: 1 } : {}),
  }
  return new TextEncoder().encode(JSON.stringify(wire))
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

/** A view from the wire, or null for anything that isn't one (another version, a field missing or out of range). */
export function decodeHostView(payload: Uint8Array): HostView | null {
  let raw: Record<string, unknown>
  try {
    raw = JSON.parse(new TextDecoder().decode(payload))
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object' || raw.v !== 1) return null
  const { s, t, la, lo, h, sp, a, b, p, m, r, ath } = raw
  if (![s, t, la, lo, h, sp, a, b, p].every(finite)) return null
  const [seq, at, latitude, longitude, lift, span, aspect, bearing, pitch] = [s, t, la, lo, h, sp, a, b, p] as number[]
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180 || span <= 0 || aspect <= 0 || pitch < 0 || pitch > 90 || lift < 0) return null
  const basemap = BASEMAPS.includes(m as HostViewBasemap) ? (m as HostViewBasemap) : 'world'
  const turned = bearing >= 0 && bearing < 360 ? bearing : ((bearing % 360) + 360) % 360
  const athlete = typeof ath === 'string' && ath.length > 0 && ath.length <= 64 ? ath : null
  return { seq, at, latitude, longitude, lift, span, aspect, bearing: turned, pitch, athlete, basemap, settled: r === 1 }
}

/**
 * Whether two poses differ by more than anyone would see: a tenth of a metre, a tenth of a percent of the span, a
 * twentieth of a degree, or another athlete's card.
 */
export function hostViewMoved(a: HostViewPose, b: HostViewPose): boolean {
  const metresPerDegree = 111_320
  const north = (a.latitude - b.latitude) * metresPerDegree
  const east = (a.longitude - b.longitude) * metresPerDegree * Math.cos((a.latitude * Math.PI) / 180)
  const turned = Math.abs(((a.bearing - b.bearing + 540) % 360) - 180)
  return (
    Math.hypot(north, east) > Math.max(0.1, a.span * 0.0005) ||
    Math.abs(a.span - b.span) > a.span * 0.001 ||
    Math.abs(a.lift - b.lift) > 0.5 ||
    Math.abs(a.aspect - b.aspect) > 0.002 ||
    turned > 0.05 ||
    Math.abs(a.pitch - b.pitch) > 0.05 ||
    (a.athlete ?? null) !== (b.athlete ?? null)
  )
}

/**
 * How far from the point a follower stands. The host's span always fits the follower's height. Looking straight down
 * the frame is a map, so a narrower screen stands back until the host's width fits too ("contain"): an upright phone
 * following a wide desktop shows the whole of the host's map. Looking across the land, standing back would move the
 * camera itself (in a low view, off the world's edge), so a narrower screen keeps the host's place and shows the middle
 * of their frame. In between it stands back by the share of the extra the view looks down (cos² of the pitch).
 */
export function followDistance(view: Pick<HostViewPose, 'span' | 'aspect' | 'pitch'>, fovDegrees: number, aspect: number): number {
  const tanHalf = Math.tan((fovDegrees * Math.PI) / 360)
  const fitHeight = view.span / (2 * tanHalf)
  const fitWidth = (view.span * view.aspect) / (2 * tanHalf * Math.max(aspect, 0.1))
  const down = Math.cos((view.pitch * Math.PI) / 180) ** 2
  return fitHeight + Math.max(0, fitWidth - fitHeight) * down
}

/** Where a follower's map finds the host's view: the latest one, and the latest settled one (for reduced motion). */
export interface HostViewStore {
  latest: HostView | null
  settled: HostView | null
}

export function createHostViewStore(): HostViewStore {
  return { latest: null, settled: null }
}

/** Takes a view in, unless it is older than what the store has. True when it did. */
export function receiveHostView(store: HostViewStore, view: HostView): boolean {
  if (store.latest && view.seq <= store.latest.seq) return false
  store.latest = view
  if (view.settled) store.settled = view
  return true
}

export function clearHostViewStore(store: HostViewStore): void {
  store.latest = null
  store.settled = null
}

/**
 * Where a host's map offers its view: their world or Mapbox map sets `read` while it is the live map on their screen
 * (not the swap card's picture, behind another tab or under a full view) and clears it otherwise. Subscribers hear it
 * come and go.
 */
export interface HostViewSource {
  read: (() => HostViewPose | null) | null
  basemap: HostViewBasemap
  set(read: (() => HostViewPose | null) | null, basemap?: HostViewBasemap): void
  subscribe(listener: () => void): () => void
}

export function createHostViewSource(): HostViewSource {
  const listeners = new Set<() => void>()
  const source: HostViewSource = {
    read: null,
    basemap: 'world',
    set(read, basemap = 'world') {
      if (source.read === read && source.basemap === basemap) return
      source.read = read
      source.basemap = basemap
      listeners.forEach((listener) => listener())
    },
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
  return source
}
